import type { CatalogueType as ConfigCatalogueType } from '@/config/types'
import type { PublicationCode } from '@/scopes/common/domain/models'
import type { FiltersInQuickSearch } from '@/scopes/search/quick-search/domain/types'
import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle } from '@sunwebgroup/ui-components/uds'
import { ComponentType, use, useEffect, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import { useCMSPageField } from '@/cms/fields'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { CatalogueType } from '@/scopes/common/domain/enums/CatalogueType'
import { getPublicationCodeByCatalogueAndSiteName } from '@/scopes/common/domain/functions/publicationCodes'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { getDefaultQuickSearchSelection } from '@/scopes/search/domain/policies/defaultFilters'
import { DepartureAirportFilter } from '@/scopes/search/quick-search/components/Filters/DepartureAirportFilter'
import { DepartureDateFilter } from '@/scopes/search/quick-search/components/Filters/DepartureDateFilter'
import { DestinationFilter } from '@/scopes/search/quick-search/components/Filters/DestinationFilter'
import { DurationFilter } from '@/scopes/search/quick-search/components/Filters/DurationFilter'
import { ParticipantsFilter } from '@/scopes/search/quick-search/components/Filters/ParticipantsFilter'
import { TransportTypeFilter } from '@/scopes/search/quick-search/components/Filters/TransportTypeFilter'
import QuickSearchBarResponsive from '@/scopes/search/quick-search/components/QuickSearchBarResponsive'
import QuickSearchToggle from '@/scopes/search/quick-search/components/QuickSearchToggle'
import {
  isLoadingSelector,
  searchLinkSelector,
  totalResultsSelector,
} from '@/scopes/search/quick-search/components/selectors'
import { quickSearchActions } from '@/scopes/search/quick-search/domain/actions/quickSearchActions'
import {
  createQuickSearchFilterClickEvent,
  QuickSearchCTAClickEvent,
} from '@/scopes/search/quick-search/domain/analytics'
import { getFiltersForQuickSearch } from '@/scopes/search/quick-search/domain/policies/filters'
import { getCatalogues } from '@/scopes/search/quick-search/domain/policies/sites'
import { createQuickSearchApiClient } from '@/scopes/search/quick-search/infrastructure/api/quickSearchApiClient'
import {
  createQuickSearchResponseMapper,
} from '@/scopes/search/quick-search/infrastructure/api/quickSearchResponseMapper'
import { quickSearchStore } from '@/scopes/search/quick-search/infrastructure/store/store'
import { useQuickSearchSelector } from '@/scopes/search/quick-search/infrastructure/store/useQuickSearchSelector'
import { Hero } from './Hero'
import { QuickSearchActionsContext } from './QuickSearchActionsContext'

const QuickSearchButton = styled(Button)`
  width: 100%;
  margin-top: ${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    margin-left: ${space('primitives_space_8')};
    flex-shrink: 0;
    width: unset;
    margin-top: unset;
    min-width: 175px;
  }

  &:hover:not(:disabled) {
    color: ${tok('primitives_color_neutral_0')};
  }
`

const StyledLoadingSpinner = styled(IndeterminateProgressCircle)`
  grid-column: span 2;
`

interface QuickSearchProps {
  catalogueType: CatalogueType
}

interface QuickSearchComponentProps {
  catalogue: ConfigCatalogueType
  showToggles?: boolean
}

const FILTER_COMPONENTS_MAP = {
  DestinationFilter,
  DepartureDateFilter,
  DurationFilter,
  ParticipantsFilter,
  TransportTypeFilter,
  DepartureAirportFilter,
} as const satisfies Record<string, ComponentType<any>>

type FilterName = keyof typeof FILTER_COMPONENTS_MAP

export default function QuickSearch({ catalogueType }: QuickSearchProps) {
  let content

  if (catalogueType === CatalogueType.None) {
    content = <QuickSearchWithToggles />
  }
  else {
    const mappedCatalogueType = catalogueType === CatalogueType.Summer ? 'summer' : 'winter'
    content = <QuickSearchComponent catalogue={mappedCatalogueType} showToggles={false} />
  }

  return (
    <>
      {content}
    </>
  )
}

function QuickSearchWithToggles() {
  const siteName = useConfiguration('siteName')
  const [defaultCatalogue] = getCatalogues(siteName)
  return <QuickSearchComponent catalogue={defaultCatalogue} showToggles />
}

function QuickSearchComponent({ catalogue, showToggles = false }: QuickSearchComponentProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { reset } = useDepartureDateStore()
  const siteName = useConfiguration('siteName')
  const contextItemId = useCMSPageField('HomePage.quickSearch.datasourceId') ?? ''

  const initialPublicationCode = getPublicationCodeByCatalogueAndSiteName(catalogue, siteName)
  const [publicationCode, setPublicationCode] = useState<PublicationCode>(initialPublicationCode)
  const [filterComponents, setFilterComponents] = useState<FilterName[]>(() =>
    getFiltersForQuickSearch(initialPublicationCode) as FilterName[],
  )

  const { setPublicationCode: setContextPublicationCode } = use(CatalogueDetailsContext)

  const quickSearchActionsMemo = useMemo(() => {
    return quickSearchActions({
      quickSearchApiClient: createQuickSearchApiClient({
        quickSearchFilterResponseMapper: createQuickSearchResponseMapper(),
      }),
      store: quickSearchStore,
    })
  }, [])
  const { initQuickSearch, filterConfirmed, switchPublicationCode } = quickSearchActionsMemo

  function handleFilterConfirm(
    name: FilterName,
    selection: any,
    sendEvent: ReturnType<typeof useAnalytics>['sendEvent'],
  ) {
    filterConfirmed({ type: name.replace('Filter', '') as keyof FiltersInQuickSearch, selection })

    const result
    = Array.isArray(selection)
      ? selection.join(', ')
      : selection?.name || selection?.distribution || selection || ''

    sendEvent(
      createQuickSearchFilterClickEvent({
        element: 'dropdown',
        type: name.toLowerCase().replace('filter', ''),
        result,
      }),
    )
  }

  useEffect(() => {
    initQuickSearch({
      publicationCode: initialPublicationCode,
      contextItemId,
      initialSelection: getDefaultQuickSearchSelection(initialPublicationCode),
    })
  }, [])

  useEffect(() => {
    setFilterComponents(getFiltersForQuickSearch(publicationCode) as FilterName[])
    setContextPublicationCode(publicationCode)
  }, [publicationCode])

  const isLoading = useQuickSearchSelector(isLoadingSelector)
  const totalResults = useQuickSearchSelector(totalResultsSelector)
  const searchLink = useQuickSearchSelector(searchLinkSelector)

  const disableSearchButton = isLoading || totalResults === 0

  const actionsContextValue = useMemo(
    () => ({
      initQuickSearch,
      filterConfirmed,
      switchPublicationCode,
    }),
    [initQuickSearch, filterConfirmed, switchPublicationCode],
  )

  async function onSwitchToggle(
    newCode: PublicationCode,
    selection: Partial<FiltersInQuickSearch>,
  ) {
    reset()
    setPublicationCode(newCode)
    await switchPublicationCode(newCode, selection)
  }

  return (
    <QuickSearchActionsContext
      value={actionsContextValue}
    >
      <Hero
        useGenericTitle={showToggles}
        catalogue={catalogue}
        filters={filterComponents.length}
        initialPublicationCode={initialPublicationCode}
      >
        {showToggles && (
          <QuickSearchToggle isLoading={isLoading} onSwitch={onSwitchToggle} />
        )}

        <QuickSearchBarResponsive disabled={isLoading}>
          {filterComponents.map((name) => {
            const Filter = FILTER_COMPONENTS_MAP[name]
            if (!Filter)
              return null

            if (name === 'DepartureDateFilter') {
              return (
                <DepartureDateFilter
                  key={name}
                  onConfirm={({ departureDate }) =>
                    handleFilterConfirm(name, departureDate, sendEvent)}
                  onReset={() => {
                    filterConfirmed({ type: 'DepartureDate', selection: undefined })
                    sendEvent(
                      createQuickSearchFilterClickEvent({
                        element: 'calendar',
                        type: 'departure date',
                        result: '',
                      }),
                    )
                  }}
                />
              )
            }

            return (
              <Filter
                key={name}
                onConfirm={selection =>
                  handleFilterConfirm(name, selection, sendEvent)}
                {...(('onReset' in Filter ? { onReset: undefined } : {}) as any)}
              />
            )
          })}

          <QuickSearchButton
            data-qa="quick-search-submit-button"
            disabled={disableSearchButton}
            forwardedAs={disableSearchButton ? 'button' : 'a'}
            href={disableSearchButton ? undefined : searchLink}
            onClick={() => sendEvent(QuickSearchCTAClickEvent)}
          >
            {isLoading
              ? (
                  <StyledLoadingSpinner />
                )
              : (
                  intl.formatMessage(
                    { id: 'quicksearch_results' },
                    { nrOfResults: totalResults },
                  )
                )}
          </QuickSearchButton>
        </QuickSearchBarResponsive>
      </Hero>
    </QuickSearchActionsContext>
  )
}
